import { CircularProgressWithLabel } from '@buildwise/libs/ui';
import useFilesQuery, { FileType } from '@buildwise/pages/companyFiles/hooks/useFilesQuery';
import FileIcon from '@buildwise/pages/search/components/FileIcon';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { FILES } from 'constants/routes';
import { Link } from 'react-router-dom';
import { catchErrorMessage, formatDate } from 'utility/helpers';
import { UploadingFileType } from './CompanyFilesWidget';
import { useCallback, useMemo, useState } from 'react';
import PdfViewer from '@buildwise/pages/search/PdfViewer';
import { MenuItemType } from 'components/drawer/RenderMenu';
import DownloadLineIcon from 'remixicon-react/DownloadLineIcon';
import useUserData from 'hooks/useUserData';
import axios from 'axios';
import DeleteBinLineIcon from 'remixicon-react/DeleteBinLineIcon';
import CompanyAPI from 'api/CompanyAPI';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import SelectMenu from 'components/common/menu/SelectMenu';
import MoreFillIcon from 'remixicon-react/MoreFillIcon';

type RecentFilesProps = {
  uploadingFiles: UploadingFileType[];
};

function RecentFiles({ uploadingFiles }: RecentFilesProps) {
  const { files, updateFilesCache } = useFilesQuery({ level: 'company', shouldFetch: false });
  const { companyId, isNovaOrCompanyAdmin, user } = useUserData();
  const theme = useTheme();
  const [removingFileId, setRemovingFileId] = useState('');
  const [openPdfViewer, setOpenPdfViewer] = useState(false);
  const [onHoverFileId, setOnHoverFileId] = useState('');
  const [selectedFile, setSelectedFile] = useState<{
    name: string;
    size: number;
    url: string;
  } | null>(null);
  const recentFiles = useMemo(() => {
    const documents: FileType[] = [];
    for (let i = 0; i < files.length; i++) {
      if (documents.length > 4) {
        break;
      }
      if (files[i].documentType !== 'folder') {
        documents.push(files[i]);
      }
    }

    return documents;
  }, [files]);

  async function downloadFile(file) {
    if (file.documentURL && file.documentName) {
      try {
        let fileURL = file.documentURL;

        if (file.documentType === 'pdf') {
          const { data: fileBlob } = await axios(file.documentURL, { responseType: 'blob' });
          fileURL = URL.createObjectURL(fileBlob);
        }

        let anchorEle = document.createElement('a');
        anchorEle.href = fileURL;
        anchorEle.download = file.documentName;
        anchorEle.click();
      } catch (error) {
        console.warn('FILE_DOWNLOAD_ERROR', error);
      }
    }
  }

  const removeFile = async (file: FileType) => {
    try {
      if (file.id) {
        setRemovingFileId(file?.id);
        const response = await CompanyAPI.removeFiles(companyId, {
          ids: {
            ids: [file?.id],
          },
          fileReferenceObject: {
            objectId: companyId,
            objectName: 'COMPANY',
            objectType: 'COMPANY_FILES',
          },
        });
        updateFilesCache({ action: 'remove', removeFileIds: [file?.id] });
        if (response?.data?.successResponse) {
          toast.success('File deleted successfully');
        }
      }
    } catch (error) {
      toast.error(catchErrorMessage(error));
    } finally {
      setRemovingFileId('');
    }
  };

  const menu = useCallback(
    (file: FileType) => {
      const MENU: MenuItemType[] = [
        {
          title: 'Download',
          icon: <DownloadLineIcon size={17} />,
          onClick: () => downloadFile(file),
        },
        {
          title: 'Delete',
          titleColor: theme.palette.custom.redWine,
          icon: <DeleteBinLineIcon size={17} color={theme.palette.custom.redWine} />,
          onClick: () => removeFile(file),
          isHidden: !(isNovaOrCompanyAdmin || file.createdBy === user?.username),
        },
      ];
      return MENU.filter((item) => !item.isHidden);
    },
    [downloadFile, removeFile, isNovaOrCompanyAdmin, user?.username],
  );

  const handleFileClick = (file: FileType) => {
    if (
      file?.documentName &&
      (file?.documentName?.toLowerCase()?.endsWith('.pdf') || file?.documentPDF)
    ) {
      setOpenPdfViewer(true);
      setSelectedFile({
        name: file.documentName,
        size: file?.documentSize || 0,
        url: file?.documentPDF || file?.documentURL || '',
      });
    } else {
      window.open(file.documentURL);
    }
  };

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography
          sx={(theme) => ({
            color: theme.palette.custom.tuna,
            fontWeight: 700,
            fontSize: 14,
          })}
        >
          Recent Files
        </Typography>
        <Link to={FILES} state={{ showBackNavigation: true }}>
          <Typography
            sx={(theme) => ({
              color: theme.palette.custom.link,
              fontWeight: 700,
              fontSize: 14,
              '&:hover': {
                textDecoration: 'underline',
              },
            })}
          >
            See All
          </Typography>
        </Link>
      </Box>

      <Box component="ul" sx={{ listStyle: 'none', p: 0, my: 1 }}>
        {uploadingFiles.map((uploadFile) => (
          <Box key={uploadFile.documentName} component="li" my={1} overflow="hidden">
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center" maxWidth="70%">
                <Box flex={1}>
                  <FileIcon fileType={uploadFile.documentType} />
                </Box>
                <Typography
                  sx={(theme) => ({
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    ml: 0.5,
                    fontSize: 14,
                  })}
                  title={uploadFile.documentName}
                >
                  {uploadFile.documentName}
                </Typography>
              </Box>
              <CircularProgressWithLabel
                sx={(theme) => ({
                  color: theme.palette.custom.pirateGold,
                  '& svg': { overflow: 'visible' },
                  '& svg circle': {
                    strokeWidth: 6,
                    strokeLinecap: 'round',
                  },
                })}
                size={20}
                value={uploadFile.uploadedPercentage}
                hideLabel
              />
            </Box>
          </Box>
        ))}
        {recentFiles.map((file) => (
          <Box
            key={file.documentName}
            component="li"
            my={1}
            overflow="hidden"
            onMouseEnter={() => {
              setOnHoverFileId(file.id);
            }}
            onMouseLeave={() => {
              setOnHoverFileId('');
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center" maxWidth="70%">
                <Box flex={1}>
                  <FileIcon fileType={file.documentType} />
                </Box>
                <Typography
                  sx={(theme) => ({
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    ml: 0.5,
                    fontSize: 14,
                  })}
                  title={file.documentName}
                  onClick={() => handleFileClick(file)}
                >
                  {file.documentName}
                </Typography>
              </Box>
              {removingFileId === file.id ? (
                <CircularProgress
                  sx={(theme) => ({ color: theme.palette.custom.black })}
                  size={20}
                />
              ) : file.id === onHoverFileId && menu(file)?.length > 0 ? (
                <SelectMenu menu={menu(file)}>
                  <IconButton size="small" title="More Actions">
                    <MoreFillIcon size={20} color={theme.palette.custom.lightBlack} />
                  </IconButton>
                </SelectMenu>
              ) : (
                <Typography
                  variant="caption"
                  sx={(theme) => ({ fontSize: 12, whiteSpace: 'nowrap' })}
                >
                  {formatDate(file.createdAt, true)}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {openPdfViewer && selectedFile && (
        <PdfViewer open={openPdfViewer} setOpen={setOpenPdfViewer} file={selectedFile} />
      )}
    </>
  );
}

export default RecentFiles;
